import { useState, useEffect } from 'react';
import {
  useSourceSettings,
  useUpdateSourceSettings,
} from '../hooks/useSourceSettings';

// Bing result count options (10-100 in increments of 10)
const BING_RESULT_OPTIONS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

export function SourceSettingsEditor() {
  const { data: settings, isLoading, error, refetch } = useSourceSettings();
  const updateSettings = useUpdateSourceSettings();

  const [bingResultCount, setBingResultCount] = useState(20);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (settings) {
      setBingResultCount(settings.bingResultCount);
      setHasChanges(false);
    }
  }, [settings]);

  const handleBingResultCountChange = (value: number) => {
    setBingResultCount(value);
    setHasChanges(value !== settings?.bingResultCount);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleReset = () => {
    if (settings) {
      setBingResultCount(settings.bingResultCount);
      setHasChanges(false);
      setSaveError(null);
      setSaveSuccess(false);
    }
  };

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync({
        bingResultCount,
      });
      setSaveSuccess(true);
      setHasChanges(false);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save source settings');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 text-center">
        <p className="text-red-400">Failed to load source settings</p>
        <button type="button" onClick={() => refetch()} className="btn-secondary mt-4">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Source Settings</h2>
          <p className="text-gray-400">
            Configure global settings for content sources
          </p>
        </div>
        {settings && (
          <p className="text-sm text-gray-500">
            Last updated: {formatDate(settings.updatedAt)}
          </p>
        )}
      </div>

      {/* Bing News Settings */}
      <div className="card">
        <h3 className="mb-4 text-lg font-semibold text-white">Bing News</h3>

        <div className="space-y-4">
          <div>
            <label htmlFor="bing-result-count" className="mb-2 block text-sm font-medium text-gray-300">
              Number of Results to Fetch
            </label>
            <select
              id="bing-result-count"
              value={bingResultCount}
              onChange={(e) => handleBingResultCountChange(Number(e.target.value))}
              className="input max-w-xs"
            >
              {BING_RESULT_OPTIONS.map((count) => (
                <option key={count} value={count}>
                  {count} results
                </option>
              ))}
            </select>
            <p className="mt-2 text-sm text-gray-500">
              The number of news articles to fetch from Bing News for each topic.
              More results provide more content choices but take longer to process.
            </p>
          </div>

          {/* Info Box */}
          <div className="rounded-md bg-blue-500/10 border border-blue-500/30 p-4">
            <h4 className="mb-2 text-sm font-medium text-blue-400">How it works</h4>
            <ul className="space-y-1 text-sm text-gray-400">
              <li>- Bing News returns approximately 10 results per page</li>
              <li>- Setting 20 results will fetch 2 pages in parallel</li>
              <li>- Setting 100 results will fetch 10 pages in parallel</li>
              <li>- Results are cached for 1 hour to reduce API calls</li>
              <li>- Only the top 5 most relevant articles are used for each episode</li>
            </ul>
          </div>

          {/* Performance Note */}
          <div className="rounded-md bg-yellow-500/10 border border-yellow-500/30 p-4">
            <h4 className="mb-2 text-sm font-medium text-yellow-400">Performance considerations</h4>
            <p className="text-sm text-gray-400">
              Higher result counts mean more pages to fetch and more articles to scrape.
              The default of 20 results is a good balance between content variety and processing time.
              Consider increasing only if you're seeing duplicate content across episodes.
            </p>
          </div>

          {/* Status Messages */}
          {saveError && (
            <div className="rounded-md bg-red-500/20 p-3 text-red-400">
              {saveError}
            </div>
          )}
          {saveSuccess && (
            <div className="rounded-md bg-green-500/20 p-3 text-green-400">
              Source settings saved successfully!
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 border-t border-gray-700 pt-4">
            {hasChanges && (
              <span className="mr-auto text-sm text-yellow-400">
                You have unsaved changes
              </span>
            )}
            <button
              type="button"
              onClick={handleReset}
              disabled={!hasChanges || updateSettings.isPending}
              className="btn-secondary"
            >
              Discard Changes
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!hasChanges || updateSettings.isPending}
              className="btn-primary"
            >
              {updateSettings.isPending ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
